import { useEffect } from 'react';
import { XMarkIcon, CreditCardIcon } from '@heroicons/react/24/outline';
import { trackEvent, currentLocationTag } from '../../lib/analytics';
import { useFlag } from '../../hooks/useFeatureFlags';

interface ChoosePlanPromptProps {
  onDismiss: () => void;
}

/**
 * ChoosePlanPrompt (task 465) is shown when innovate-service returns
 * 402 { error: 'no_active_plan' } — the account has NO plan at all, so every
 * AI action fails closed server-side. Sibling of UpgradePrompt (same
 * placement, rendered by EntitlementGuard), but the remedy is different:
 * choosing a plan is a decision, so the CTA links to the Subscription page's
 * plan catalog — deliberately NOT straight to Stripe checkout.
 *
 * Role-aware: plan selection is an owner concern (BillingPage `isOwner`
 * precedent). A non-owner is directed to their account owner; a missing role
 * claim (old token) falls through to the owner view — the server-side gate is
 * the real enforcement.
 */
export default function ChoosePlanPrompt({ onDismiss }: ChoosePlanPromptProps) {

  // Billing is an owner concern; the server enforces it with
  // 403 owner_required. The courtesy check now keys off
  // innovate.fe.billing.page, whose role-owner cohort carries the rule
  // (task 692) — no role claim is read here.
  const isNonOwner = !useFlag('innovate.fe.billing.page');

  // Impression event — once per appearance of the prompt.
  useEffect(() => {
    trackEvent('choose_plan_prompt_shown', {
      location: currentLocationTag(),
      is_owner: !isNonOwner,
    });
  }, [isNonOwner]);

  return (
    <div
      role="alert"
      data-testid="choose-plan-prompt"
      className="rounded-lg border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4"
    >
      <div className="flex items-start gap-3">
        <CreditCardIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">
            Your account doesn't have a plan yet
          </p>
          {isNonOwner ? (
            <p
              className="mt-1 text-sm text-gray-600 dark:text-gray-300"
              data-testid="choose-plan-prompt-member-note"
            >
              AI features are disabled until a plan is chosen — ask your account
              owner to choose a plan.
            </p>
          ) : (
            <>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                AI features are disabled until you choose one. Pick a plan on
                Settings → Subscription to enable them.
              </p>
              <div className="mt-3">
                {/* Plain anchor (not router Link): like UpgradePrompt, this
                    renders in error states and must stay free of router/flag
                    coupling. Routes to the plan catalog, never checkout. */}
                <a
                  href="/settings/subscription"
                  data-testid="choose-plan-button"
                  onClick={() =>
                    trackEvent('choose_plan_prompt_cta_clicked', {
                      location: currentLocationTag(),
                    })
                  }
                  className="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors"
                >
                  Choose a plan
                </a>
              </div>
            </>
          )}
        </div>
        <button
          onClick={onDismiss}
          data-testid="choose-plan-prompt-dismiss"
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
